/**
 * Cleaning Window Card V2
 *
 * Renders one cleaning window: day selection, session type (single/multiple),
 * and nested sessions with primary + alternative time ranges.
 *
 * Design: SOLID, clean code, performance (useCallback/memo), scalability
 * (constants, small components), maintainability and readability.
 */

import React, { useCallback, useMemo } from 'react';
import { Trash2, X } from 'lucide-react';
import PropTypes from 'prop-types';
import { AddPlaceholder } from '../common/AddPlaceHolderV2';
import { generateSessionId, calculateWindowFrequency } from '../../utils/sessionMigrations';

// -----------------------------------------------------------------------------
// Constants (Single source of truth, easy to tune)
// -----------------------------------------------------------------------------

const DAYS_OF_WEEK = [
  { short: 'M', full: 'Monday' },
  { short: 'T', full: 'Tuesday' },
  { short: 'W', full: 'Wednesday' },
  { short: 'T', full: 'Thursday' },
  { short: 'F', full: 'Friday' },
  { short: 'S', full: 'Saturday' },
  { short: 'S', full: 'Sunday' },
];

const MAX_SESSIONS = 5;
const MAX_ALTERNATIVE_RANGES = 2;
const DEFAULT_PRIMARY_RANGE = { from: '08:00', to: '12:00' };
const DEFAULT_ALTERNATIVE_RANGE = { from: '13:00', to: '17:00' };

// -----------------------------------------------------------------------------
// Custom hook: all window update actions (stable references via useCallback)
// -----------------------------------------------------------------------------

function useCleaningWindowCardActions(window, onUpdate, onChange, options = {}) {
  const { availableDays = [], totalAssignedDays = 0, maxDaysAllowed = 7 } = options;

  const notifyAndUpdate = useCallback(
    (nextWindow) => {
      onChange?.();
      onUpdate(nextWindow);
    },
    [onUpdate, onChange]
  );

  const toggleDay = useCallback(
    (day) => {
      if (window.days.includes(day)) {
        notifyAndUpdate({
          ...window,
          days: window.days.filter((d) => d !== day),
        });
      } else {
        const canAdd =
          availableDays.includes(day) && totalAssignedDays < maxDaysAllowed;
        if (canAdd) {
          notifyAndUpdate({
            ...window,
            days: [...window.days, day],
          });
        }
      }
    },
    [window, notifyAndUpdate, availableDays, totalAssignedDays, maxDaysAllowed]
  );

  const handleSessionTypeChange = useCallback(
    (newType) => {
      if (newType === 'single') {
        const firstSession = window.sessions[0];
        const restSessions = window.sessions.slice(1, 1 + MAX_ALTERNATIVE_RANGES);
        notifyAndUpdate({
          ...window,
          sessionType: 'single',
          sessions: [
            {
              ...firstSession,
              alternativeRanges: [
                ...(firstSession.alternativeRanges ?? []),
                ...restSessions.map((s) => s.primaryRange),
              ].slice(0, MAX_ALTERNATIVE_RANGES),
            },
          ],
        });
      } else {
        notifyAndUpdate({ ...window, sessionType: 'multiple' });
      }
    },
    [window, notifyAndUpdate]
  );

  const addSession = useCallback(() => {
    if (window.sessionType !== 'multiple' || window.sessions.length >= MAX_SESSIONS) return;
    notifyAndUpdate({
      ...window,
      sessions: [
        ...window.sessions,
        {
          id: generateSessionId(),
          name: `Session ${window.sessions.length + 1}`,
          primaryRange: { ...DEFAULT_PRIMARY_RANGE },
          alternativeRanges: [],
        },
      ],
    });
  }, [window, notifyAndUpdate]);

  const removeSession = useCallback(
    (sessionId) => {
      if (window.sessions.length <= 1) return;
      notifyAndUpdate({
        ...window,
        sessions: window.sessions.filter((s) => s.id !== sessionId),
      });
    },
    [window, notifyAndUpdate]
  );

  const updateSessionName = useCallback(
    (sessionId, name) => {
      notifyAndUpdate({
        ...window,
        sessions: window.sessions.map((s) =>
          s.id === sessionId ? { ...s, name } : s
        ),
      });
    },
    [window, notifyAndUpdate]
  );

  const updatePrimaryRange = useCallback(
    (sessionId, field, value) => {
      notifyAndUpdate({
        ...window,
        sessions: window.sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                primaryRange: { ...s.primaryRange, [field]: value },
              }
            : s
        ),
      });
    },
    [window, notifyAndUpdate]
  );

  const updateAlternativeRange = useCallback(
    (sessionId, rangeIndex, field, value) => {
      notifyAndUpdate({
        ...window,
        sessions: window.sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                alternativeRanges: (s.alternativeRanges ?? []).map((range, i) =>
                  i === rangeIndex ? { ...range, [field]: value } : range
                ),
              }
            : s
        ),
      });
    },
    [window, notifyAndUpdate]
  );

  const addAlternativeRange = useCallback(
    (sessionId) => {
      const session = window.sessions.find((s) => s.id === sessionId);
      if (!session || (session.alternativeRanges?.length ?? 0) >= MAX_ALTERNATIVE_RANGES) return;
      const alternatives = session.alternativeRanges ?? [];
      notifyAndUpdate({
        ...window,
        sessions: window.sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                alternativeRanges: [...alternatives, { ...DEFAULT_ALTERNATIVE_RANGE }],
              }
            : s
        ),
      });
    },
    [window, notifyAndUpdate]
  );

  const removeAlternativeRange = useCallback(
    (sessionId, rangeIndex) => {
      notifyAndUpdate({
        ...window,
        sessions: window.sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                alternativeRanges: (s.alternativeRanges ?? []).filter(
                  (_, i) => i !== rangeIndex
                ),
              }
            : s
        ),
      });
    },
    [window, notifyAndUpdate]
  );

  const updateWindowName = useCallback(
    (name) => {
      notifyAndUpdate({ ...window, name });
    },
    [window, notifyAndUpdate]
  );

  return {
    toggleDay,
    handleSessionTypeChange,
    addSession,
    removeSession,
    updateSessionName,
    updatePrimaryRange,
    updateAlternativeRange,
    addAlternativeRange,
    removeAlternativeRange,
    updateWindowName,
  };
}

// -----------------------------------------------------------------------------
// Subcomponents (focused responsibility, memo for performance)
// -----------------------------------------------------------------------------

const inputTimeClasses =
  'w-[115px] border border-gray-300 rounded-lg px-2.5 py-1.5 font-[\'Roboto:Regular\',sans-serif] text-[12px] focus:border-[#61b8e7] focus:outline-none focus:ring-1 focus:ring-[#61b8e7]';
const labelClasses = "font-['Roboto:Regular',sans-serif] text-[12px] text-gray-600 flex-shrink-0";

const TimeRangeInput = React.memo(function TimeRangeInput({
  from,
  to,
  onChangeFrom,
  onChangeTo,
  onRemove,
  label = 'Primary',
}) {
  return (
    <div className="flex items-center gap-2">
      <span className={`${labelClasses} w-20`}>{label}:</span>
      <span className={`${labelClasses} w-11`}>From</span>
      <input
        type="time"
        value={from}
        onChange={(e) => onChangeFrom(e.target.value)}
        className={inputTimeClasses}
        aria-label={`${label} from`}
      />
      <span className={labelClasses}>To</span>
      <input
        type="time"
        value={to}
        onChange={(e) => onChangeTo(e.target.value)}
        className={inputTimeClasses}
        aria-label={`${label} to`}
      />
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
          title="Remove alternative range"
          aria-label="Remove range"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
});

TimeRangeInput.propTypes = {
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  onChangeFrom: PropTypes.func.isRequired,
  onChangeTo: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  label: PropTypes.string,
};

const DaySelector = React.memo(function DaySelector({
  days,
  availableDays,
  totalAssignedDays,
  maxDaysAllowed,
  onToggleDay,
}) {
  const isEmpty = days.length === 0;

  return (
    <div className="mb-5">
      <div className="mb-2">
        <label className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700">
          Select Days
        </label>
      </div>

      <div className="flex gap-2 mb-2">
        {DAYS_OF_WEEK.map((day, index) => {
          const isSelected = days.includes(day.full);
          const isAvailable = availableDays.includes(day.full) || isSelected;
          const isDisabled =
            !isAvailable || (!isSelected && totalAssignedDays >= maxDaysAllowed);

          return (
            <button
              key={`${day.full}-${index}`}
              type="button"
              onClick={() => !isDisabled && onToggleDay(day.full)}
              disabled={isDisabled}
              className={`
                w-10 h-10 rounded-full font-['Roboto:Medium',sans-serif] text-[14px] transition-all
                ${
                  isSelected
                    ? 'bg-[#61b8e7] text-white'
                    : isDisabled
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400 cursor-pointer'
                }
              `}
              aria-pressed={isSelected}
              aria-label={`${day.full}${isSelected ? ' selected' : ''}`}
            >
              {day.short}
            </button>
          );
        })}
      </div>

      {isEmpty && (
        <p className="font-['Roboto:Regular',sans-serif] text-[12px] text-red-600">
          Please select at least 1 day for this window
        </p>
      )}
    </div>
  );
});

DaySelector.propTypes = {
  days: PropTypes.arrayOf(PropTypes.string).isRequired,
  availableDays: PropTypes.arrayOf(PropTypes.string).isRequired,
  totalAssignedDays: PropTypes.number.isRequired,
  maxDaysAllowed: PropTypes.number.isRequired,
  onToggleDay: PropTypes.func.isRequired,
};

const SessionTypeToggle = React.memo(function SessionTypeToggle({
  windowId,
  sessionType,
  onChange,
}) {
  return (
    <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <label className="block font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mb-3">
        Session Type
      </label>

      <div className="space-y-2">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="radio"
            name={`session-type-${windowId}`}
            checked={sessionType === 'single'}
            onChange={() => onChange('single')}
            className="mt-0.5 w-4 h-4 text-[#61b8e7] focus:ring-[#61b8e7] focus:ring-offset-0"
            aria-label="Single session per day"
          />
          <div className="flex-1">
            <div className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-900">
              Single session per day
            </div>
            <div className="font-['Roboto:Regular',sans-serif] text-[12px] text-gray-600 mt-0.5">
              Clean once, janitor chooses from available time options
            </div>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="radio"
            name={`session-type-${windowId}`}
            checked={sessionType === 'multiple'}
            onChange={() => onChange('multiple')}
            className="mt-0.5 w-4 h-4 text-[#61b8e7] focus:ring-[#61b8e7] focus:ring-offset-0"
            aria-label="Multiple sessions per day"
          />
          <div className="flex-1">
            <div className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-900">
              Multiple sessions per day
            </div>
            <div className="font-['Roboto:Regular',sans-serif] text-[12px] text-gray-600 mt-0.5">
              Clean multiple times, all sessions required
            </div>
          </div>
        </label>
      </div>
    </div>
  );
});

SessionTypeToggle.propTypes = {
  windowId: PropTypes.string.isRequired,
  sessionType: PropTypes.oneOf(['single', 'multiple']).isRequired,
  onChange: PropTypes.func.isRequired,
};

const SessionItem = React.memo(function SessionItem({
  session,
  sessionIndex,
  canRemove,
  onUpdateName,
  onUpdatePrimaryRange,
  onUpdateAlternativeRange,
  onAddAlternativeRange,
  onRemoveAlternativeRange,
  onRemoveSession,
}) {
  const alternatives = session.alternativeRanges ?? [];
  const canAddAlternative = alternatives.length < MAX_ALTERNATIVE_RANGES;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
      <div className="flex items-center justify-between mb-3">
        <input
          type="text"
          value={session.name ?? ''}
          onChange={(e) => onUpdateName(e.target.value)}
          placeholder={`Session ${sessionIndex + 1}`}
          className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-900 bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-[#61b8e7] rounded px-2 py-1"
          aria-label={`Session ${sessionIndex + 1} name`}
        />
        {canRemove && (
          <button
            type="button"
            onClick={onRemoveSession}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Remove session"
            aria-label="Remove session"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="space-y-2.5">
        <TimeRangeInput
          from={session.primaryRange.from}
          to={session.primaryRange.to}
          onChangeFrom={(value) => onUpdatePrimaryRange('from', value)}
          onChangeTo={(value) => onUpdatePrimaryRange('to', value)}
          label="Primary"
        />

        {alternatives.map((altRange, altIndex) => (
          <TimeRangeInput
            key={altIndex}
            from={altRange.from}
            to={altRange.to}
            onChangeFrom={(value) => onUpdateAlternativeRange(altIndex, 'from', value)}
            onChangeTo={(value) => onUpdateAlternativeRange(altIndex, 'to', value)}
            onRemove={() => onRemoveAlternativeRange(altIndex)}
            label={`Alternative ${altIndex + 1}`}
          />
        ))}

        {canAddAlternative && (
          <AddPlaceholder
            variant="link"
            label="Add Alternative Range"
            onClick={onAddAlternativeRange}
          />
        )}
      </div>
    </div>
  );
});

SessionItem.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    primaryRange: PropTypes.shape({
      from: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    }).isRequired,
    alternativeRanges: PropTypes.arrayOf(
      PropTypes.shape({
        from: PropTypes.string.isRequired,
        to: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  sessionIndex: PropTypes.number.isRequired,
  canRemove: PropTypes.bool.isRequired,
  onUpdateName: PropTypes.func.isRequired,
  onUpdatePrimaryRange: PropTypes.func.isRequired,
  onUpdateAlternativeRange: PropTypes.func.isRequired,
  onAddAlternativeRange: PropTypes.func.isRequired,
  onRemoveAlternativeRange: PropTypes.func.isRequired,
  onRemoveSession: PropTypes.func.isRequired,
};

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

function CleaningWindowCard({
  window,
  availableDays,
  totalAssignedDays,
  onUpdate,
  onRemove,
  canRemove,
  maxDaysAllowed,
  onChange,
}) {
  const actions = useCleaningWindowCardActions(window, onUpdate, onChange, {
    availableDays,
    totalAssignedDays,
    maxDaysAllowed,
  });

  const isEmpty = window.days.length === 0;
  const frequency = useMemo(
    () => calculateWindowFrequency(window),
    [window]
  );

  const containerClasses = isEmpty
    ? 'border-amber-300 bg-amber-50/30'
    : 'border-gray-200 bg-white';

  const isMultiple = window.sessionType === 'multiple';
  const canAddSession = isMultiple && window.sessions.length < MAX_SESSIONS;

  return (
    <div
      className={`border-2 rounded-lg p-5 transition-colors ${containerClasses}`}
      data-testid="cleaning-window-card"
    >
      <div className="flex items-center justify-between mb-4">
        <input
          type="text"
          value={window.name ?? ''}
          onChange={(e) => actions.updateWindowName(e.target.value)}
          className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-black border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-[#61b8e7] rounded px-2 py-1"
          placeholder="Window Name"
          aria-label="Window name"
        />
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove window"
            aria-label="Remove window"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <DaySelector
        days={window.days}
        availableDays={availableDays}
        totalAssignedDays={totalAssignedDays}
        maxDaysAllowed={maxDaysAllowed}
        onToggleDay={actions.toggleDay}
      />

      <SessionTypeToggle
        windowId={window.id}
        sessionType={window.sessionType}
        onChange={actions.handleSessionTypeChange}
      />

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700">
            Time Schedule
          </label>
          {window.days.length > 0 && (
            <span className="font-['Roboto:Regular',sans-serif] text-[12px] text-gray-500">
              {frequency} cleanings/week
            </span>
          )}
        </div>

        <div className="space-y-4">
          {window.sessions.map((session, sessionIndex) => (
            <SessionItem
              key={session.id}
              session={session}
              sessionIndex={sessionIndex}
              canRemove={window.sessions.length > 1}
              onUpdateName={(name) => actions.updateSessionName(session.id, name)}
              onUpdatePrimaryRange={(field, value) =>
                actions.updatePrimaryRange(session.id, field, value)
              }
              onUpdateAlternativeRange={(rangeIndex, field, value) =>
                actions.updateAlternativeRange(session.id, rangeIndex, field, value)
              }
              onAddAlternativeRange={() => actions.addAlternativeRange(session.id)}
              onRemoveAlternativeRange={(rangeIndex) =>
                actions.removeAlternativeRange(session.id, rangeIndex)
              }
              onRemoveSession={() => actions.removeSession(session.id)}
            />
          ))}
        </div>

        {canAddSession && (
          <AddPlaceholder
            variant="link"
            label="Add Session"
            onClick={actions.addSession}
            className="mt-3"
          />
        )}
      </div>
    </div>
  );
}

CleaningWindowCard.propTypes = {
  window: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    days: PropTypes.arrayOf(PropTypes.string).isRequired,
    sessionType: PropTypes.oneOf(['single', 'multiple']).isRequired,
    sessions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        primaryRange: PropTypes.shape({
          from: PropTypes.string.isRequired,
          to: PropTypes.string.isRequired,
        }).isRequired,
        alternativeRanges: PropTypes.array,
      })
    ).isRequired,
  }).isRequired,
  availableDays: PropTypes.arrayOf(PropTypes.string).isRequired,
  totalAssignedDays: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
  maxDaysAllowed: PropTypes.number.isRequired,
  onChange: PropTypes.func,
};

export { CleaningWindowCard };
